import { T, Var } from 'gt-react';
import { CheckCircleIcon, ProhibitIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { CollectionThreadResolution } from '@erp/rpc';

/**
 * Validation strip shown at the top of a `deduction` collection thread. Surfaces
 * the deduction amount + reason and the approval state, and offers Approve /
 * Charge-back actions an AR rep uses to validate the claim. Approve resolves the
 * thread `deduction_approved`; Charge-back resolves it `deduction_charged_back`.
 *
 * Money/reason come from the thread metadata block DeductionDetectionService
 * writes when the thread is auto-opened from a remittance.
 */

interface Props {
  metadata: Record<string, unknown>;
  resolution: CollectionThreadResolution | null;
  isResolved: boolean;
  isPending: boolean;
  onApprove: () => void;
  onChargeBack: () => void;
}

function readString(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number') return String(value);
  return null;
}

function formatMoney(raw: string | null): string {
  if (raw == null) return '—';
  const num = Number(raw);
  if (Number.isNaN(num)) return raw;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

export function DeductionValidationCard({
  metadata,
  resolution,
  isResolved,
  isPending,
  onApprove,
  onChargeBack,
}: Props) {
  const amount = formatMoney(readString(metadata.deductionAmount));
  const reason = readString(metadata.deductionReason);
  const approvalState = readString(metadata.approvalState);

  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">
            <T context="label, deduction amount">Deduction</T>
          </div>
          <div className="text-lg font-semibold">{amount}</div>
          {reason && (
            <div className="text-sm text-muted-foreground mt-0.5">
              <Var>{reason}</Var>
            </div>
          )}
        </div>

        {isResolved ? (
          <Badge
            variant={
              resolution === 'deduction_approved' ? 'default' : 'secondary'
            }
          >
            {resolution === 'deduction_approved' ? (
              <T context="badge, deduction approved">Approved</T>
            ) : resolution === 'deduction_charged_back' ? (
              <T context="badge, deduction charged back">Charged back</T>
            ) : (
              <T context="badge, deduction resolved">Resolved</T>
            )}
          </Badge>
        ) : (
          <Badge variant="secondary">
            {approvalState === 'pending_validation' ? (
              <T context="badge, deduction pending validation">
                Pending validation
              </T>
            ) : approvalState ? (
              <Var>{approvalState}</Var>
            ) : (
              <T context="badge, deduction open">Open</T>
            )}
          </Badge>
        )}
      </div>

      {!isResolved && (
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" disabled={isPending} onClick={onApprove}>
            <CheckCircleIcon className="size-4" />
            <T context="button, approve deduction">Approve</T>
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={isPending}
            onClick={onChargeBack}
          >
            <ProhibitIcon className="size-4" />
            <T context="button, charge back deduction">Charge back</T>
          </Button>
        </div>
      )}
    </div>
  );
}
